import { TouchableOpacity } from 'react-native'
import { Box, colors, Spread, Stack, Text } from '../../../components/DesignSystem'
import Formatter from '../../../helpers/Formatter'
import { useAppSelector } from '../../../state/hooks'
import { useState } from 'react'

type OrderSummaryProps = {
	prices: {
		subTotal: number
		taxes: number
		serviceCharge: number
		serviceChargeGST: number
		serviceChargePST: number
		bagFee: number
		deliveryFee: number
		tip: number
		discount: number
		redeemedGiftCard: number
		total: number
		taxesList: { name: string; amount: number }[]
	}
	useProductCheckout: boolean
}

export default function OrderSummary({ prices, useProductCheckout }: OrderSummaryProps) {
	const { isDelivery } = useAppSelector(state => state.persisted)
	const { deliveryEnabled } = useAppSelector(state => state.settings)
	const { giftCard } = useAppSelector(state => state.checkout)
	const [showTaxDetails, setShowTaxDetails] = useState(false)
	return (
		<Box background="grayTranslucent" borderRadius="medium" padding="lg">
			<Stack gap="lg">
				<Spread>
					<Text>Subtotal</Text>
					<Text>{Formatter.currencyFormat(prices.subTotal)}</Text>
				</Spread>

				<TouchableOpacity onPress={() => setShowTaxDetails(!showTaxDetails)}>
					<Spread>
						<Text>Taxes & Fees</Text>
						<Text>
							{Formatter.currencyFormat(
								prices.taxes +
									Math.round(prices.serviceCharge * 100) / 100 +
									Math.round(prices.serviceChargeGST * 100) / 100 +
									Math.round(prices.serviceChargePST * 100) / 100
							)}
						</Text>
					</Spread>
				</TouchableOpacity>

				{showTaxDetails &&
				((prices.taxesList && prices.taxesList.length > 0) || prices.serviceCharge !== 0) ? (
					<Box paddingX="md">
						<Stack gap="xs">
							{prices.taxesList.map((tax, index) => (
								<Text
									key={tax.name}
									level="small"
								>{`${tax.name} (${Formatter.currencyFormat(tax.amount / 100)})`}</Text>
							))}
							{prices.serviceCharge !== 0 && (
								<Text level="small">{`Service Fee (${Formatter.currencyFormat(
									Math.round(prices.serviceCharge * 100) / 100 +
										Math.round(prices.serviceChargeGST * 100) / 100 +
										Math.round(prices.serviceChargePST * 100) / 100
								)})`}</Text>
							)}
						</Stack>
					</Box>
				) : null}

				{prices.bagFee !== 0 && (
					<Spread>
						<Text>Bag Fee</Text>
						<Text>{Formatter.currencyFormat(prices.bagFee)}</Text>
					</Spread>
				)}

				{!useProductCheckout && isDelivery && deliveryEnabled && (
					<Spread>
						<Text>Delivery Fee</Text>
						<Text>{Formatter.currencyFormat(prices.deliveryFee / 100)}</Text>
					</Spread>
				)}

				{prices.tip > 0 && (
					<Spread>
						<Text>Tips</Text>
						<Text>{Formatter.currencyFormat(prices.tip)}</Text>
					</Spread>
				)}
				{prices.discount !== 0 && (
					<Spread>
						<Text>Discounts</Text>
						<Text>- {Formatter.currencyFormat(prices.discount * -1)}</Text>
					</Spread>
				)}

				{giftCard && (
					<Spread>
						<Text>Gift Card</Text>
						<Text>-{Formatter.currencyFormat(prices.redeemedGiftCard)}</Text>
					</Spread>
				)}

				<Spread>
					<Text level="subheadline" weight="semibold">
						Total Due
					</Text>
					<Text level="subheadline" weight="semibold" tone="positive">
						{Formatter.currencyFormat(prices.total)}
					</Text>
				</Spread>
			</Stack>
		</Box>
	)
}
